/* eslint-disable prefer-destructuring */
/* eslint-disable eqeqeq */
import { batch } from 'react-redux';
import i18next from 'i18next';
import React from 'react';
import * as R from 'ramda';
import { renameKeysWith } from 'ramda-adjunct';

import * as shoppingCartActionTypes from 'constants/ShoppingCart';
import * as salesAPI from 'services/ErplyAPI/sales';
import { modalPages as modals } from 'constants/modalPage';
import {
  checkAvailability,
  checkStockFarFromMinimum,
  getAppliedCoupons,
  getAppliedPromotions,
  getHasProductDiscountReasonByIndex,
  getHasProductReturnReasonByIndex,
  getHasProductsInShoppingCart,
  getHasTempAmount,
  getIsProductNonDiscountable,
  getLastProductIndex,
  getProductInOrderByIndex,
  getProductsInShoppingCart,
  getShouldCalculateOffline,
  getReturnReasonByOrderIndex,
  prepareShoppingCartForCalculate,
  getTotalCountInCart,
  getDiscountableTotal,
} from 'reducers/ShoppingCart';
import {
  getIsDefaultCustomer,
  getSelectedCustomerID,
} from 'reducers/customerSearch';
import {
  getClientCode,
  getLoggedInEmployeeID,
  getSessionKey,
} from 'reducers/Login';
import { getProductByID } from 'reducers/cachedItems/products';
import { getSelectedPos } from 'reducers/PointsOfSale';
import {
  getAllowEditingExistingLayaways,
  getCustomPromptsForProduct,
  getDisableProductAddedNotification,
  getSetting,
  getSerialNumberFormatterFn,
  getShowPricesWithTax,
  getSerialNumberTitle,
  getSkipCalculateShoppingCartOnChange,
  getPriceEditingAllowedForExchangesAndReturns,
  getDiscountLimit,
  getShoppingCartRowMergingCriteria,
  getTextAroundSerialNumber,
} from 'reducers/configs/settings';
import {
  getCurrentSalesDocument,
  getHasCurrentSalesDocument,
  getIsAReturn,
  getIsCurrentSaleAReturn,
} from 'reducers/sales';
import {
  setCurrentSalesDocPayments,
  setCurrentSalesDocument,
  setIsCurrentSaleAReturn,
  startNewSale,
  checkForLimitAmountPerSale,
  setUsedRewardPoints,
} from 'actions/sales';
import { setCustomer } from 'actions/customerSearch';
import { ErplyAttributes } from 'utils';
import { setShoppingCartTooltip } from 'actions/UI';
import { getPluginLifecycleHook } from 'reducers/Plugins';
import { getWeightedProductUnits } from 'reducers/productUnitsDB';
import { REASONS } from 'constants/reasonCodesDB';
import { getReasonCodes } from 'reducers/reasonCodesDB';
import { getShoppingCartTooltip } from 'reducers/UI/customerTooltip';
import { getMessageIfShoppingcartShouldBeBlocked } from 'reducers/modalPage';
import { openManagerOverride } from 'containers/Forms/ManagerOverride/actions';
import { getHasPayButtonBeenClicked } from 'reducers/Payments';
import { parseRowsWithContainers } from 'utils/containers';
import {
  getCustomPromptValuesAndSeparatorUsedFromItem,
  getEmbeddedProductWithCustomPromptInformation,
  getRowAttributesPerRowIdFromSale,
} from 'utils/customPrompts';

import { openModalPage } from './modalPage';
import { addError, addSuccess, addWarning, dismissAll } from './Error';
import { createConfirmation } from './Confirmation';
import { getProductsUniversal } from './productsDB';
import { getRightsForUserID } from './Login';
import { withContainingProgress } from './actionUtils';

const calculateOffline = shouldCalculate => ({
  type: shoppingCartActionTypes.SET_CALCULATE_OFFLINE,
  payload: shouldCalculate,
});

const recalculateAfter = action => {
  return async dispatch => {
    await dispatch(action);
    return dispatch(calculate());
  };
};

export const addProductSuccess = (product, options = {}) => {
  return {
    type: shoppingCartActionTypes.ADD_PRODUCT_SUCCESS,
    payload: {
      product,
      options,
    },
  };
};

export const addProductFailure = error => {
  return {
    type: shoppingCartActionTypes.ADD_PRODUCT_FAILURE,
    payload: error,
  };
};

export const addMultiProductsSuccess = products => {
  return {
    type: shoppingCartActionTypes.ADD_MULTI_PRODUCTS_SUCCESS,
    payload: products,
  };
};

export const addMultiProductsFailure = error => {
  return {
    type: shoppingCartActionTypes.ADD_MULTI_PRODUCTS_FAILURE,
    payload: error,
  };
};

// Order of execution in this action has a purposes, please be cautions if refactoring
/**
 * @param orderIndex - products orderIndex in the salesDoc
 * @param params.showWarning - set to false if you have some warning in case document gets cleared, otherwise true or don't send
 */

export const removeProduct = (orderIndex, params = { showWarning: true }) => {
  return async (dispatch, getState) => {
    if (!orderIndex) {
      console.error('removeProductAction called without orderIndex');
      return;
    }
    const { before, on, after } = getPluginLifecycleHook('onRemoveOrder')(
      getState(),
    );
    await dispatch(before({ orderIndex }));

    const state = getState();
    const isCurrentSaleAReturn = getIsCurrentSaleAReturn(state);
    const hasCurrentSalesDocument = getHasCurrentSalesDocument(state);
    const shouldRemoveGroupedProducts = getSetting(
      'touchpos_remove_grouped_products_in_shopping_cart',
    )(state);
    const products = getProductsInShoppingCart(state);

    const discountReason = getHasProductDiscountReasonByIndex(orderIndex)(
      getState(),
    );
    const returnReason = getHasProductReturnReasonByIndex(orderIndex)(
      getState(),
    );

    if (discountReason) {
      dispatch(clearDiscountReason(orderIndex));
    }

    if (returnReason) {
      dispatch(clearReturnReason(orderIndex));
    }

    const canEditProductsInLayways = getAllowEditingExistingLayaways(state);
    const currentSalesDocument = getCurrentSalesDocument(state);
    const { type, invoiceState } = currentSalesDocument;
    const isLayaway = type === 'PREPAYMENT' && invoiceState === 'READY';
    const isInvoice = type === 'INVOICE' && invoiceState === 'READY';

    if (!canEditProductsInLayways && isLayaway) {
      return dispatch(addWarning(i18next.t('alerts:layawayCartEditLocked')));
    }

    if (isInvoice) {
      return dispatch(addWarning(i18next.t('alerts:invoiceCartEditLocked')));
    }

    if (shouldRemoveGroupedProducts) {
      await Promise.all(
        products
          .filter(pr => String(pr.parentRowID) === String(orderIndex)) // orderIndex can be string or number
          .map(({ orderIndex }) => dispatch(removeProduct(orderIndex))),
      ).catch(e => console.error('Failed to remove products', products, e));
    }

    // New getState to exclude auto-removed products from ↑
    const remainingProducts = products.filter(p => p.orderIndex !== orderIndex);
    const noNegativeProducts = remainingProducts.every(
      p => Number(p.amount) > 0,
    );
    const noProducts = remainingProducts.length === 0;
    try {
      await dispatch(on({ orderIndex }));
    } catch (e) {
      return;
    }

    // Prompt a confirmation on removing last product in cart
    if (
      orderIndex &&
      ((noNegativeProducts && isCurrentSaleAReturn) ||
        (noProducts && hasCurrentSalesDocument))
    ) {
      if (params.showWarning) {
        dispatch(addWarning(i18next.t('alerts:clearSale')));
      }
      dispatch(startNewSale());
    }
    batch(() => {
      dispatch({
        type: shoppingCartActionTypes.REMOVE_PRODUCT,
        payload: orderIndex,
      });
      dispatch(calculate());
    });
    // Plugin hook
    dispatch(after({ orderIndex }));
  };
};

export const increaseOrderAmount = orderIndex => async (dispatch, getState) => {
  const { amount } = getProductInOrderByIndex(orderIndex)(getState());
  await dispatch(updateOrderAmount(Number(amount) + 1 || 1, orderIndex));
};

export const decreaseOrderAmount = orderIndex => async (dispatch, getState) => {
  const { amount } = getProductInOrderByIndex(orderIndex)(getState());
  await dispatch(updateOrderAmount(Number(amount) - 1 || -1, orderIndex));
};

export const setTempAmount = (amount, orderIndex) => {
  return {
    type: shoppingCartActionTypes.SET_TEMP_AMOUNT,
    payload: { amount, orderIndex },
  };
};

export const setSelectedOrder = orderIndex => {
  return {
    type: shoppingCartActionTypes.SET_SELECTED_ORDER,
    payload: orderIndex,
  };
};

export const updateOrderAmount = (
  amount,
  orderIndex,
  shouldCalculateAfter = true,
) => async (dispatch, getState) => {
  try {
    const state = getState();

    const shoppingCartItems = getProductsInShoppingCart(state);
    const currentProduct = shoppingCartItems.find(
      product => product.orderIndex === orderIndex,
    );
    if (Number(amount) === Number(currentProduct.amount)) {
      return; // No change necessary
    }

    const returnReasonCodes = getReasonCodes(REASONS.RETURN)(state);

    const {
      products: [selectedProduct],
    } = await dispatch(
      getProductsUniversal({ productID: currentProduct.productID }),
    );

    const shouldAddProduct = await dispatch(
      checkForLimitAmountPerSale(
        selectedProduct,
        shoppingCartItems,
        amount,
        orderIndex,
      ),
    );

    if (!shouldAddProduct) {
      throw new Error('Product not added to cart due to limit per sale');
    }

    const { before, on, after } = getPluginLifecycleHook('onUpdateOrderAmount')(
      state,
    );
    try {
      await dispatch(before({ amount, orderIndex }));
    } catch (e) {
      return undefined; // No error if cancelled by plugin
    }
    const order = getProductInOrderByIndex(orderIndex)(state);
    const returnReason = getReturnReasonByOrderIndex(order.orderIndex)(state);
    const isCurrentSaleAReturn = getIsCurrentSaleAReturn(state);

    const { productID, amount: currentAmount } = order;
    const totalInCart = getTotalCountInCart(productID)(state);
    const originalProduct = getProductByID(productID)(state);
    // Prevent setting negative amount to non-refundable product
    if (originalProduct.nonRefundable && Number(amount) < 0) {
      return dispatch(
        addWarning(
          i18next.t('alerts:nonRefundableProductError', {
            name: originalProduct.name,
          }),
        ),
      );
    }
    const canEditProductsInLayways = getAllowEditingExistingLayaways(state);
    const currentSalesDocument = getCurrentSalesDocument(state);
    const { type, invoiceState } = currentSalesDocument;
    const isLayaway = type === 'PREPAYMENT' && invoiceState === 'READY';
    const isInvoice = type === 'INVOICE' && invoiceState === 'READY';

    if (!canEditProductsInLayways && isLayaway) {
      return dispatch(
        addWarning(i18next.t('shoppingCart:alerts.layawayCartEditLocked')),
      );
    }

    if (isInvoice) {
      return dispatch(addWarning(i18next.t('alerts:invoiceCartEditLocked')));
    }

    if (getIsCurrentSaleAReturn(getState())) {
      if (Number(currentAmount) < 0) {
        return dispatch(
          addWarning(
            i18next.t('shoppingCart:alerts.referencedReturnRowsLocked'),
          ),
        );
      }
      if (Number(amount) < 0) {
        return dispatch(
          addWarning(
            i18next.t('shoppingCart:alerts.referencedReturnRowsExclusive'),
          ),
        );
      }
    }

    // Only trigger override if triggering return (from positive to negative)
    if (Number(amount) < 0 && Number(currentAmount) > 0) {
      await i18next.loadNamespaces('managerOverride');
      await dispatch(
        openManagerOverride([
          {
            key: 'returnWithoutReceipt',
            text: i18next.t('managerOverride:reasons.returnWithoutReceipt'),
            isPermitted: user =>
              getRightsForUserID(Number(user.userID)).then(
                ({ rightMakePOSReturnsWithoutReceipt }) =>
                  !!Number(rightMakePOSReturnsWithoutReceipt),
              ),
          },
        ]),
      );
    }

    if (
      Number(amount) < 0 &&
      returnReasonCodes.length > 0 &&
      !returnReason &&
      !isCurrentSaleAReturn
    ) {
      await dispatch(
        openModalPage({
          component: modals.ProductReturnReasons,
          isPopup: true,
          props: {
            reasons: returnReasonCodes,
            order,
          },
        }),
      );
    }

    const pluginOverride = await dispatch(
      on({ amount, orderIndex }, { amount, orderIndex }),
    );

    // changing product's amount so that the store needs to give it out
    if (
      // return changed to ordinary sale
      (Number(currentAmount) < 0 && Number(amount) > 0) ||
      // ordinary sale
      Number(amount) > 0
    ) {
      if (!checkAvailability(productID, amount, orderIndex)(getState())) {
        const warningType = getSetting('touchpos_out_of_stock_warning')(
          getState(),
        );
        const translate = (subKey, ...rest) =>
          i18next.t(`shoppingCart:alerts.productOutOfStock.${subKey}`, ...rest);
        const transData = {
          /** Currect actual stock (including products already in the cart but not sold yet) */
          stock: selectedProduct.free,
          /** The amount of this product that would be in the shopping cart if we continue */
          inCartAfter:
            totalInCart - Number(currentAmount) + Number(amount || 1),
          /**
           * The name of the product which stock is checked
           * NB: Not the overwritten itemName of this row, since stock is calculated for the product card
           */
          productName: selectedProduct.name,
        };
        switch (warningType) {
          case 'block':
            dispatch(addError(translate('block', transData)));
            return undefined;
          case 'confirmation':
            await new Promise((resolve, reject) =>
              dispatch(
                createConfirmation(resolve, reject, {
                  title: translate('confirmation.title', transData),
                  body: translate('confirmation.body', transData),
                }),
              ),
            );
            break;
          case 'warning':
            dispatch(
              addWarning(translate('warning', transData), {
                selfDismiss: 3000,
              }),
            );
            break;
          default:
            // off
            break;
        }
      }

      const farFromMinimum = checkStockFarFromMinimum(
        productID,
        amount,
      )(getState());

      if (!farFromMinimum) {
        dispatch(
          createConfirmation(
            () => {
              // do nothing
            },
            null,
            {
              title: i18next.t(
                `shoppingCart:alerts.productCloseToOutOfStock.title`,
              ),
              body: i18next.t(
                `shoppingCart:alerts.productCloseToOutOfStock.body`,
              ),
            },
          ),
        );
      }
    }

    dispatch({
      type: shoppingCartActionTypes.UPDATE_ORDER_AMOUNT,
      payload: {
        amount: pluginOverride?.amount ?? amount,
        orderIndex: pluginOverride?.orderIndex ?? orderIndex,
      },
    });
    await dispatch(after({ amount, orderIndex }, pluginOverride));
    if (shouldCalculateAfter) {
      return dispatch(calculate());
    }
  } catch (err) {
    console.error('Failed to update order amount', err);
    if (err.message?.length) {
      return dispatch(
        addError(
          i18next.t('shoppingCart:alerts.updateAmountFailed', {
            context: 'err',
            error: err.message,
          }),
        ),
      );
    }
    return dispatch(
      addError(i18next.t('shoppingCart:alerts.updateAmountFailed')),
    );
  }
};

export const updateOrderDiscount = (
  manualDiscount,
  orderIndex,
  shouldCalculateAfter = true,
) => async (dispatch, getState) => {
  const { rows = [] } = getCurrentSalesDocument(getState());
  const isReturn = getIsAReturn(getState());
  const isReferencedReturn = isReturn && rows.length > 0;
  const allowOvercharge = getSetting('brazil_allow_overcharge', 1)(getState());
  const lowerLimit = allowOvercharge ? Number.NEGATIVE_INFINITY : 0;
  const allowPriceEditingForExchangesAndReferencedReturns = getPriceEditingAllowedForExchangesAndReturns(
    getState(),
  );

  const payload =
    isReferencedReturn && !allowPriceEditingForExchangesAndReferencedReturns
      ? {}
      : {
          manualDiscount: Math.max(manualDiscount, lowerLimit),
          orderIndex,
        };

  dispatch({
    type: shoppingCartActionTypes.UPDATE_ORDER_DISCOUNT,
    payload,
  });
  if (shouldCalculateAfter) {
    return dispatch(calculate());
  }
};

export const updateProductNotes = (notes, orderIndex) =>
  recalculateAfter({
    type: shoppingCartActionTypes.UPDATE_PRODUCT_NOTES,
    payload: { notes, orderIndex },
  });

export const updateOrderPrice = (price, priceVAT, orderIndex) =>
  recalculateAfter({
    type: shoppingCartActionTypes.UPDATE_ORDER_PRICE,
    payload: { price, priceVAT, orderIndex },
  });

export const clearOrderPrice = orderIndex =>
  recalculateAfter({
    type: shoppingCartActionTypes.CLEAR_ORDER_PRICE,
    payload: { orderIndex },
  });

/**
 * Override adding product container(deposit fee product)
 *
 * @param orderIndex {number|string} - order index of the product int he shopping cart
 * @param shouldNotHaveContainer {boolean | undefined} - should the container be added to shopping cart
 * @returns {{payload: {orderIndex: *}, type: *}}
 */
export const updateProductContainerOverride = (
  orderIndex,
  shouldHaveContainer,
) =>
  recalculateAfter({
    type: shoppingCartActionTypes.UPDATE_NO_PRODUCT_CONTAINER,
    payload: { orderIndex, addContainerOverride: shouldHaveContainer },
  });

export const resetShoppingCart = () => async (dispatch, getState) => {
  const { before, on, after } = getPluginLifecycleHook('onResetShoppingCart')(
    getState(),
  );

  await dispatch(before());

  await dispatch(on());

  dispatch({
    type: shoppingCartActionTypes.RESET_SHOPPING_CART,
  });

  await dispatch(after());
};

export const calculateSuccess = calculated => {
  return {
    type: shoppingCartActionTypes.CALCULATE_SUCCESS,
    payload: calculated,
  };
};

export const calculateFailure = error => {
  return {
    type: shoppingCartActionTypes.CALCULATE_FAILURE,
    payload: error,
  };
};

export const setOrderEmployeeID = (employeeID, orderIndex) =>
  recalculateAfter({
    type: shoppingCartActionTypes.SET_EMPLOYEE,
    payload: {
      orderIndex,
      employeeID,
    },
  });

/**
 * Calculate Shopping Cart action
 * @param extraParams
 * @param extraParams.forceCalculate {boolean} - force calculate even if pos_brazil_do_not_calculate_shopping_cart_on_change is enabled
 * @returns {(function(*, *): Promise<undefined|*>)|*}
 */
export const calculate = ({ ...extraParams } = {}) => {
  return async (dispatch, getState) => {
    const hasTooltip = getShoppingCartTooltip(getState());
    const hasTempAmount = getHasTempAmount(getState());
    if (
      getSkipCalculateShoppingCartOnChange(getState()) &&
      !extraParams.forceCalculate
    ) {
      return;
    }

    // No products, promotions, nor coupons in cart. No need to bother with the API or any other fancy logic
    // Just reset the cart to its default empty state
    if (
      getProductsInShoppingCart(getState()).length === 0 &&
      getAppliedCoupons(getState()).length === 0 &&
      getAppliedPromotions(getState()).length === 0
    ) {
      dispatch({ type: shoppingCartActionTypes.RESET_SHOPPING_CART });
      return;
    }
    batch(() => {
      dispatch({
        type: shoppingCartActionTypes.CALCULATE,
      });
      hasTempAmount && dispatch(setTempAmount(false, false));
      hasTooltip && dispatch(setShoppingCartTooltip(null));
    });
    try {
      const state = getState();
      const { before, on, after } = getPluginLifecycleHook('onCalculate')(
        state,
      );
      await dispatch(before({ ...extraParams }));
      const { calcIndex } = state.shoppingCart;
      let rows = await dispatch(prepareShoppingCartForCalculate);
      const shouldCalculateOffline = getShouldCalculateOffline(getState());
      const customerID = getSelectedCustomerID(state);
      const clientCode = getClientCode(state);
      const isReturn = getIsAReturn(state);
      const sessionKey = getSessionKey(state);
      const { warehouseID, pointOfSaleID } = getSelectedPos(state);
      const currentSalesDocument = getCurrentSalesDocument(state);
      const {
        taxExemptCertificateNumber: taxExNr,
        isPartialExempt,
        type,
        invoiceState,
        baseDocumentIDs,
        id,
        rows: curSalesDocRows,
      } = currentSalesDocument;

      rows = await dispatch(on({ ...extraParams }, rows));

      let params = {};

      const isSavedLayaway = id && type === 'PREPAYMENT';

      rows
        .map(item => {
          const isNonDiscountable = getIsProductNonDiscountable(item)(
            getState(),
          );

          if (isNonDiscountable && !item.forceDiscount && !isSavedLayaway) {
            return R.dissoc('discount', item);
          }

          /**
           * When order/layaway/account sale is picked up promotion discount and manual discounts
           * are combined and sum percentage is returned from API in the discount field.
           *
           * Pending sales have their own promotion discount handling (they reject discount field).
           */
          const getDiscount = () => {
            if (!id) {
              return item.discount ?? item.manualDiscount;
            }
            if (!item.manualDiscount && !item.discount) {
              return null;
            }

            const discount =
              (1 - (item.manualDiscount ?? 0) / 100) *
              (1 - (item.discount ?? 0) / 100);

            return Math.min((1 - discount) * 100, 100);
          };

          const getPrice = () => {
            if (isReturn) {
              const existingRow = curSalesDocRows?.find(
                origRow => origRow.stableRowID === item.stableRowID,
              );
              const price = existingRow?.price;
              return price ?? item.price;
            }
            return item.price;
          };

          const discount = getDiscount();

          return R.pipe(
            R.ifElse(
              R.always(R.isNil(discount)),
              R.dissoc('discount'),
              R.assoc('discount', discount),
            ),
            // Calculate shopping cart ignores these fields, thus they are removed
            R.omit(['manualDiscount', 'returnReasonID', 'stableRowID']),
            R.assoc('price', getPrice()),
          )(item);
        })
        .map((row, i) => renameKeysWith(k => `${k}${i + 1}`)(row))
        .forEach(item => Object.assign(params, item));

      const manualPromotionIDs = getAppliedPromotions(
        state,
      ).flatMap(({ amount, campaignID }) => Array(amount).fill(campaignID));

      const couponIdentifiers = getAppliedCoupons(state).map(
        coup => coup.uniqueIdentifier,
      );

      if (taxExNr && !isPartialExempt) {
        params.taxExempt = 1;
        // Remove vatrateID properties as they cause api to calculate the tax
        params = R.pickBy((value, key) => !/vatrateID\d+/.test(key))(params);
      }

      if ((id && type === 'ORDER') || baseDocumentIDs) {
        params.doNotApplyPromotions = 1;
      }

      // Type === 'INVOICE' check added to fix issue in 4212
      if (
        type === 'PREPAYMENT' ||
        type === 'INVOICE' ||
        type === 'INVWAYBILL'
      ) {
        params.doNotApplyInvoiceLevelPromotions = 1;
      }

      const [data] = await salesAPI.calculateShoppingCart({
        ...params,
        ...(manualPromotionIDs.length > 0 ? { manualPromotionIDs } : {}),
        ...(couponIdentifiers.length > 0 ? { couponIdentifiers } : {}),
        warehouseID,
        pointOfSaleID,
        getAutomaticCoupons: 1,
        sessionKey,
        clientCode,
        customerID,
        ...extraParams,
      });

      if (shouldCalculateOffline) {
        dispatch(calculateOffline(false));
      }

      const newCalcIndex = getState().shoppingCart.calcIndex;
      if (newCalcIndex === calcIndex) {
        const ret = {};
        rows.forEach((row, i) => {
          const [
            orderIndex,
            subOrderIndex = undefined,
          ] = `${row.orderIndex}`.split('-');
          ret[orderIndex] = ret[orderIndex] || { container: [] };
          if (subOrderIndex) {
            ret[orderIndex].container.push(data.rows[i + 1]);
          } else {
            ret[orderIndex].computed = data.rows[i + 1];
          }
        });
        await dispatch(calculateSuccess({ ...data, rows: ret, calcIndex }));
        await dispatch(
          after({ ...params, ...extraParams }, { ...data, rows: ret }),
        );

        if (data.freeExtraProductID) {
          await dispatch(
            setShoppingCartTooltip(
              data.freeExtraProductID ? (
                // eslint-disable-next-line jsx-a11y/anchor-is-valid
                <a
                  href=""
                  onClick={e => {
                    e.preventDefault();
                    dispatch(
                      addProduct({ productID: data.freeExtraProductID }),
                    );
                  }}
                >
                  {data.freeExtraNotification}
                </a>
              ) : null,
            ),
          );
        }

        return data;
      }
      // If more changes have occurred, ignore the results from this request
    } catch (e) {
      dispatch(calculateOffline(true));
      console.error('Failed to calculate shopping cart', e);
      dispatch(calculateFailure(e));
    }
  };
};

let times = 0;
const debouncedCalculate = (timeout = 200) => async dispatch => {
  times++;
  if (times < 2) {
    return dispatch(calculate());
  }
  const tempTimes = times;
  setTimeout(() => {
    if (times === tempTimes) {
      dispatch(calculate());
      times = 0;
    }
  }, timeout);
};

// TODO Especify the values and business logic from the params (documentation)
export const addProduct = (
  {
    productID,
    amount = undefined,
    price = undefined,
    basePrice = undefined,
    priceSet = undefined,
    giftCardSerial = '',
    manualDiscount = 0,
    needsWeightPopup = true,
    addContainerOverride = undefined,
    parentRowID = 0,
    notes = undefined,
    vatrateID = undefined,
    addedByScanner = false,
    allowNegativePrice = true,
    name = undefined,
  },
  { nextView = undefined, extraParams = {} } = {},
) => {
  // prettier-ignore
  const params = {
    name,
    productID,
    amount,
    price,
    basePrice,
    priceSet,
    giftCardSerial,
    manualDiscount,
    needsWeightPopup,
    addContainerOverride,
    parentRowID,
    notes,
    vatrateID,
    allowNegativePrice,
  };

  return async (dispatch, getState) => {
    const alertIfBlocked = () => {
      const err = getMessageIfShoppingcartShouldBeBlocked(getState());
      if (!err) {
        return false;
      }
      const [key, data] = err;
      dispatch(
        addError(
          i18next.t(`shoppingCart:alerts.blocked.${key}`, {
            context: data,
          }),
          {
            dismissible: true,
            selfDismiss: false,
            errorType: 'productAddedToCart',
          },
        ),
      );
      return true;
    };
    if (alertIfBlocked()) {
      return;
    }

    const state = getState();

    // do not allow user to add more products after clicking pay
    if (getHasPayButtonBeenClicked(state)) {
      return;
    }

    try {
      let {
        products: [selectedProduct],
      } = await dispatch(
        getProductsUniversal(
          { productID },
          { addToCachedItems: true, withMeta: true },
        ),
      );
      if (alertIfBlocked()) {
        return;
      }

      if (selectedProduct.productComponents?.length) {
        await dispatch(
          getProductsUniversal(
            {
              productIDs: selectedProduct.productComponents.map(
                pc => pc.componentID,
              ),
            },
            { addToCachedItems: true, withMeta: true },
          ),
        );
        if (alertIfBlocked()) {
          return;
        }
      }

      const { before, on, after } = getPluginLifecycleHook('onAddProduct')(
        getState(),
      );
      try {
        await dispatch(before({ ...params, extraParams }));
      } catch (e) {
        return;
      }
      if (alertIfBlocked()) {
        return;
      }
      const weightedUnits = getWeightedProductUnits(getState());

      const employeeID = getLoggedInEmployeeID(getState());
      let product = {
        productID,
        amount:
          weightedUnits.includes(selectedProduct.unitName) && needsWeightPopup
            ? 0
            : amount ?? 1,
        manualDiscount,
        employeeID,
        basePrice,
        price,
        parentRowID,
        notes,
        vatrateID,
        addedByScanner,
      };

      if (Object.keys(extraParams).length > 0) {
        Object.assign(product, {
          extraParams,
        });
      }

      if (name) {
        Object.assign(product, {
          name,
        });
      }

      const attrs = new ErplyAttributes(selectedProduct.attributes);
      const disallowSellingToDefaultCustomer = attrs.get(
        'disallow_selling_to_default_customer',
      );
      const isDefaultCustomer = getIsDefaultCustomer(state);
      const canEditProductsInLayways = getAllowEditingExistingLayaways(state);
      const currentSalesDocument = getCurrentSalesDocument(state);
      const shoppingCartItems = getProductsInShoppingCart(state);
      const serialNumberTitle = getSerialNumberTitle(state);
      const textAroundSerialNumber = getTextAroundSerialNumber(state);
      const { type, invoiceState } = currentSalesDocument;
      const totalInCart = getTotalCountInCart(productID)(state);
      const isLayaway = type === 'PREPAYMENT' && invoiceState === 'READY';
      const isInvoice = type === 'INVOICE' && invoiceState === 'READY';

      if (!canEditProductsInLayways && isLayaway) {
        dispatch(addWarning(i18next.t('alerts:layawayCartEditLocked')), {
          errorType: 'productAddedToCart',
        });
        return;
      }

      if (isInvoice) {
        return dispatch(addWarning(i18next.t('alerts:invoiceCartEditLocked')));
      }

      if (disallowSellingToDefaultCustomer && isDefaultCustomer) {
        dispatch(addWarning(i18next.t('alerts:noCustomerSelected')), {
          errorType: 'productAddedToCart',
        });
        return;
      }

      // Non refundable products should not be added to shopping cart with negative amount
      if (selectedProduct.nonRefundable && amount < 0) {
        return dispatch(
          addWarning(
            i18next.t('alerts:nonRefundableProductError', {
              name: selectedProduct.name,
            }),
          ),
        );
      }

      // Check for the limit amount per sale
      const shouldAddProduct = await dispatch(
        checkForLimitAmountPerSale(selectedProduct, shoppingCartItems),
      );
      if (alertIfBlocked()) {
        return;
      }

      if (!shouldAddProduct) {
        throw new Error('Product not added to cart due to limit per sale');
      }

      // For serial numbered gift cards, display serial number adding field
      if (selectedProduct.isGiftCard && !selectedProduct.isRegularGiftCard) {
        if (giftCardSerial || giftCardSerial !== '') {
          Object.assign(selectedProduct, { giftCardSerial });
          if (
            getSetting('append_gift_card_number_to_product_name')(getState())
          ) {
            Object.assign(product, {
              itemName: `${selectedProduct.name} ${giftCardSerial
                .split('')
                .map((char, i, arr) => (i > arr.length - 5 ? char : '*'))
                .join('')}`,
            });
          }
        } else {
          return dispatch(
            openModalPage({
              component: modals.giftCardSerial,
              groupID: modals.giftCardSerial,
              replace: true,
              props: { productID },
              isPopup: true,
            }),
          );
        }
      }

      // For matrix master products, do not add but display menu instead
      if (selectedProduct.variationList?.length) {
        return dispatch(
          openModalPage({
            component: modals.matrixVariations,
            groupID: modals.matrixVariations,
            replace: true,
            props: { selectedProductID: productID },
          }),
        );
      }

      // For cashier must enter price products, display price input
      if (
        selectedProduct.cashierMustEnterPrice &&
        !getIsAReturn(getState()) &&
        !priceSet
      ) {
        return dispatch(
          openModalPage({
            component: modals.cashierPrice,
            groupID: modals.cashierPrice,
            replace: true,
            props: {
              productID,
              giftCardSerial,
              allowNegativePrice,
              product: selectedProduct,
            },
            isPopup: true,
          }),
        );
      }

      let serialNumberData = '';
      if (selectedProduct.hasSerialNumbers) {
        serialNumberData = await dispatch(
          openModalPage({
            isPopup: true,
            component: modals.serialNumberPrompt,
            props: {
              productName: selectedProduct.name,
            },
          }),
        );
        if (alertIfBlocked()) {
          return;
        }
        if (serialNumberData.trim().length) {
          product.serialNumber = serialNumberData;
        }
      }

      const serialNumberFormatter = getSerialNumberFormatterFn(getState());

      // For products with custom serial(s), display serials input
      const customPrompts = getCustomPromptsForProduct(productID)(getState());

      if (customPrompts.length) {
        const customData = await dispatch(
          openModalPage({
            isPopup: true,
            component: modals.customPrompts,
            props: {
              productID,
              prompts: customPrompts,
              productName: selectedProduct.name,
            },
          }),
        );
        if (alertIfBlocked()) {
          return;
        }

        product = getEmbeddedProductWithCustomPromptInformation({
          product: { ...product, name: product.name ?? selectedProduct.name },
          newValues: customData,
          separator: textAroundSerialNumber,
          // Add serial number only if one was configured
          serialNumber:
            serialNumberTitle && serialNumberData?.length
              ? `${serialNumberTitle}: ${serialNumberData}`
              : undefined,
        });
      }
      if (!customPrompts.length && serialNumberData.length) {
        product.name = `${selectedProduct.name} ${serialNumberFormatter(
          `${serialNumberTitle}: ${serialNumberData}`,
        )}`;
        product.serialNumber = serialNumberData;
      }

      const warningType = getSetting('touchpos_out_of_stock_warning')(
        getState(),
      );

      if (
        ['block', 'confirmation', 'warning'].includes(warningType) &&
        !checkAvailability(productID, amount)(getState())
      ) {
        // if out of stock - display warning
        const translate = (subKey, ...rest) =>
          i18next.t(`shoppingCart:alerts.productOutOfStock.${subKey}`, ...rest);

        const transData = {
          /** Currect actual stock (including products already in the cart but not sold yet) */
          stock: selectedProduct.free,
          /** The amount of this product that would be in the shopping cart if we continue */
          inCartAfter: totalInCart + (amount || 1),
          /** The name of the product which stock is checked */
          productName: selectedProduct.name,
        };
        switch (warningType) {
          case 'block':
            dispatch(addError(translate('block', transData)), {
              errorType: 'productAddedToCart',
            });
            return;
          case 'confirmation':
            await new Promise((resolve, reject) =>
              dispatch(
                createConfirmation(resolve, reject, {
                  title: translate('confirmation.title', transData),
                  body: translate('confirmation.body', transData),
                }),
              ),
            );
            if (alertIfBlocked()) {
              return;
            }
            break;
          case 'warning':
            dispatch(dismissAll('productAddedToCart'));
            dispatch(
              addWarning(translate('warning', transData), {
                selfDismiss: 3000,
                errorType: 'productAddedToCart',
              }),
            );
            break;
          default:
            // off
            break;
        }
      }

      const farFromMinimum = checkStockFarFromMinimum(
        productID,
        amount,
      )(getState());
      if (!farFromMinimum) {
        dispatch(
          createConfirmation(
            () => {
              // do nothing
            },
            null,
            {
              title: i18next.t(
                `shoppingCart: alerts.productCloseToOutOfStock.title`,
              ),
              body: i18next.t(
                `shoppingCart: alerts.productCloseToOutOfStock.body`,
              ),
            },
          ),
        );
      }

      // Add the product to the cart
      let shouldMerge =
        !getSetting('touchpos_always_item_in_new_row')(getState()) &&
        !selectedProduct.cashierMustEnterPrice &&
        !giftCardSerial &&
        amount === undefined &&
        price === undefined;

      const mergeCriteria = getShoppingCartRowMergingCriteria(getState());

      if (giftCardSerial || giftCardSerial !== '') {
        Object.assign(product, { giftCardSerial });
      }
      if (selectedProduct.isRegularGiftCard) {
        const { isRegularGiftCard } = selectedProduct;
        Object.assign(product, { isRegularGiftCard });
      }
      if (selectedProduct.attributes) {
        // Shouldn't this be { attributes: selectedProduct.attributes }
        Object.assign(product, { attributes: product.attributes });
      }

      try {
        // PLUGIN - onProductAdd
        // TODO: ShouldMerge must be replaced with `options` to support any type of prop override
        ({ product, shouldMerge, selectedProduct } = await dispatch(
          on(params, { product, shouldMerge, selectedProduct }),
        ).catch(e => Symbol.for('pluginerror')));
        if (alertIfBlocked()) {
          return;
        }

        batch(async () => {
          dispatch(
            addProductSuccess(product, { merge: shouldMerge, mergeCriteria }),
          );
          dispatch(debouncedCalculate());
          dispatch(setSelectedOrder(getLastProductIndex(getState())));
          if (!getDisableProductAddedNotification(getState())) {
            dispatch(
              addSuccess(i18next.t('alerts:productAddedToDocument'), {
                selfDismiss: 1500,
                dismissible: false,
                errorType: 'productAddedToCart',
              }),
            );
          }
        });
      } catch (e) {
        if (e !== Symbol.for('pluginerror')) {
          console.error('Failed to merge product with previous row', e);
        }
        return dispatch(addProductFailure(e));
      }

      // Open the next view if one is specified
      if (nextView) {
        dispatch(openModalPage(nextView));
      }

      // Display related products popup if any exist
      const shouldDisplayRelatedProducts = !getSetting(
        'touchpos_disable_related_products_popup',
      )(getState());

      if (
        shouldDisplayRelatedProducts &&
        (selectedProduct.relatedProducts?.length ||
          selectedProduct.replacementProducts?.length)
      ) {
        dispatch(
          openModalPage({
            component: modals.relatedProducts,
            props: {
              productID,
              parentRowID: getLastProductIndex(getState()),
            },
            modalClassName: 'related-products-modal',
            groupID: 'RELATED_PRODUCTS',
          }),
        );
      }

      // if product needs custom price, display price input
      const shouldDisplayPriceInput = !getSetting(
        'touchpos_disable_zero_price_auto_open',
      )(getState());

      if (
        shouldDisplayPriceInput &&
        selectedProduct.price === 0 &&
        !selectedProduct.cashierMustEnterPrice
      ) {
        batch(async () => {
          await dispatch(
            openModalPage({
              component: modals.ProductOrderEdit,
              props: { isMatrixProduct: true },
              groupID: 'PRODUCT_VIEW',
              modalClassName: 'product-edit-modal',
            }),
          );
        });
      }
      const packages = selectedProduct.productPackages?.filter(
        p => p.packageAmount,
      );
      // Prompt user with amount selection if product has packages
      if (packages.length && selectedProduct.soldInPackages) {
        const packageAmounts = packages.map(pkg => pkg.packageAmount);
        dispatch(
          openModalPage({
            isPopup: true,
            component: modals.PackageAmountPopup,
            props: {
              amounts: packageAmounts,
              orderIndex: getLastProductIndex(getState()),
            },
          }),
        );
      }
      // Asks for product weight input
      if (
        weightedUnits.includes(selectedProduct.unitName) &&
        needsWeightPopup
      ) {
        dispatch(
          openModalPage({
            component: modals.ProductWeight,
            props: {
              orderIndex: getLastProductIndex(getState()),
              unit: selectedProduct.unitName,
            },
            modalClassName: 'product-weight-modal',
            isPopup: true,
          }),
        );
      }

      // Display warning if bundle
      if (selectedProduct.type === 'BUNDLE') {
        dispatch(
          addWarning('Bundle product selected', {
            selfDismiss: 3000,
            errorType: 'productAddedToCart',
          }),
        );
      }

      // PLUGIN - afterAddProduct
      dispatch(after(params, { product, selectedProduct }));
    } catch (e) {
      console.error('Failed to add product to cart', e);
    }
  };
};

export const addMultiProducts = (orders = []) => async (dispatch, getState) => {
  const { productsDict } = await dispatch(
    getProductsUniversal(
      { productIDs: orders.map(o => o.productID) },
      { addToCachedItems: true },
    ),
  );
  const reasonCodes = getReasonCodes(REASONS.DISCOUNT)(getState());

  const { before, on, after } = getPluginLifecycleHook('onAddMultiProducts')(
    getState(),
  );

  try {
    await dispatch(before(orders));
  } catch (e) {
    return;
  }

  await dispatch({
    type: shoppingCartActionTypes.ADD_MULTI_PRODUCTS_START,
  });

  const textFreeItems = [];

  const currentSalesDocument = getCurrentSalesDocument(getState());
  const rowAttributesPerIdFromSale = getRowAttributesPerRowIdFromSale(
    currentSalesDocument,
  );
  // For initiation if the flag "Cashier Must Enter Price" is visible, on multiple products added from a saved sale
  let masterOrders = R.pipe(
    // Filter non-refundable products
    R.filter(order => {
      // Dealing with free-tex line products. These items have an ID value of 0. They will not be found in the productsDict henceforth they need to be "stored" in a separate constant to later be passed on.
      if (order.productID !== '0') {
        const { name, nonRefundable } = productsDict[order.productID];
        if (nonRefundable && order.amount < 0) {
          dispatch(
            addWarning(i18next.t('alerts:nonRefundableProductError', { name })),
          );
          return false;
        }
        return true;
      }
      textFreeItems.push(order);
    }),
    orders =>
      parseRowsWithContainers(orders, productsDict, order => {
        // Override to false
        if (order.addContainerOverride === false) {
          return true;
        }
        // Not overridden to true and is a return
        if (order.addContainerOverride === undefined && order.amount < 0) {
          return true;
        }
        return false;
      }),
    // Expose manual discount in both fields
    R.map(({ itemName, discount, ...o }) => ({
      manualDiscount: discount,
      discount,
      name: itemName,
      ...o,
    })),
    // Attach row level attributes from sale
    R.map(row => {
      const rowAttributes = rowAttributesPerIdFromSale[row.stableRowID] ?? {};
      if (Object.keys(rowAttributes).length) {
        return {
          ...row,
          attributes: rowAttributes,
        };
      }
      return row;
    }),
    R.map(R.omit(['rowNetTotal', 'rowTotal', 'rowVAT'])),
  )(orders);

  // region Serial number logic block
  const serialNumberPromiseMap = (inputValues, mapper) => {
    const reducer = (accumulator, inputValue) =>
      accumulator.then(async acc => {
        return mapper(inputValue).then(result => {
          return acc.push(result) && acc;
        });
      });

    return inputValues.reduce(reducer, Promise.resolve([]));
  };

  const setSerialNumber = withContainingProgress(
    item => async (dispatch, getState, progress) => {
      const textAroundSerialNumber = getTextAroundSerialNumber(getState());
      const serialNumberTitle = getSerialNumberTitle(getState());
      // For products with custom serial(s), display serials input
      const customPrompts = getCustomPromptsForProduct(item.productID)(
        getState(),
      );
      if (customPrompts.length) {
        const {
          separator,
          customPromptDetails,
        } = getCustomPromptValuesAndSeparatorUsedFromItem({
          item,
          separator: textAroundSerialNumber,
          customPrompts,
        }) ?? { separator: textAroundSerialNumber };

        const promptsAlreadyFilledOut = customPrompts.every(
          p => customPromptDetails?.[p]?.length,
        );
        if (!promptsAlreadyFilledOut) {
          dispatch(progress.halt);
          const customData = await dispatch(
            openModalPage({
              isPopup: true,
              component: modals.customPrompts,
              props: {
                prompts: customPrompts,
                productName: item.name,
                initPromptValues: customPromptDetails ?? {},
              },
            }),
          );
          dispatch(progress.resume);
          const sn =
            serialNumberTitle && item.serialNumber
              ? `${serialNumberTitle}: ${item.serialNumber}`
              : undefined;
          item = getEmbeddedProductWithCustomPromptInformation({
            product: item,
            separator,
            newValues: customData,
            serialNumber: sn,
            existingSerialNumber: sn,
          });
        } else {
          item = getEmbeddedProductWithCustomPromptInformation({
            product: item,
            separator,
            newValues: customPromptDetails,
            existingSerialNumber: item.jdoc?.BrazilPOS?.serialNumber,
          });
        }
      }
      return item;
    },
  );

  // endregion
  try {
    masterOrders = await serialNumberPromiseMap(masterOrders, item =>
      dispatch(setSerialNumber(item)),
    );
    const finalFreeTextLineProducts = textFreeItems.map(item => ({
      ...item,
      name: (item.name?.trim() || item.itemName) ?? '',
    }));

    const params = await dispatch(
      on(orders, [...masterOrders, ...finalFreeTextLineProducts]),
    ).catch(() => {
      throw 'pluginerror';
    });
    await dispatch(addMultiProductsSuccess(params));
    params.forEach(param => {
      // set the discount reasons in redux for products which have a discount
      if (Number(param.amount) > 0 && param.returnReasonID) {
        const reason = reasonCodes.find(
          r => r.reasonID === Number(param.returnReasonID),
        );
        dispatch(setDiscountReason(reason, param.orderIndex));
      }
    });
    await dispatch(calculate());
    await dispatch(after(orders, params));
  } catch (e) {
    if (e !== 'pluginerror') {
      console.error('Failed to load shopping cart', e);
      throw e;
    }
  }
};

export const addReturnProducts = ({ returnDocument, orders }) => {
  return async (dispatch, getState) => {
    const { before, on, after } = getPluginLifecycleHook('onAddReturnProducts')(
      getState(),
    );
    const selectedCustomerID = getSelectedCustomerID(getState());
    try {
      await dispatch(before({ returnDocument, orders }));
      await dispatch({ type: shoppingCartActionTypes.RESET_SHOPPING_CART });
      await dispatch(
        getProductsUniversal(
          { productIDs: orders.map(o => Number(o.productID)) },
          { addToCachedItems: true },
        ),
      );
      batch(async () => {
        try {
          const { returnDocument: retDoc, orders: ord } = await dispatch(
            on({ returnDocument, orders }, { returnDocument, orders }),
          ).catch(() => {
            throw 'pluginerror';
          });
          await dispatch(setCurrentSalesDocument(retDoc));
          await dispatch(setIsCurrentSaleAReturn(true));
          await dispatch(setUsedRewardPoints(retDoc));
          await dispatch(
            setCurrentSalesDocPayments({
              documentID: retDoc.id,
              paymentIDs: retDoc.returnedPayments?.length
                ? retDoc.returnedPayments.flatMap(p => p.ids)
                : null,
            }),
          );

          // dont reset customer if that customer already selected
          if (retDoc.clientID !== selectedCustomerID) {
            await dispatch(
              setCustomer({
                data: retDoc.clientID,
                recalculateShoppingCart: false,
              }),
            );
          }
          await dispatch(addMultiProducts(ord));
          await dispatch(
            after(
              { returnDocument, orders },
              { returnDocument: retDoc, orders: ord },
            ),
          );
        } catch (error) {
          if (error !== 'pluginerror') {
            console.error('Failed to load return products', error);
          }
        }
      });
    } catch (error) {
      console.error('Failed to add return products', error);

      dispatch(addMultiProductsFailure(error));
    }
  };
};

export const updateProductOrder = ({
  name = undefined,
  amount = undefined,
  manualDiscount = undefined,
  employeeID = undefined,
  orderIndex,
  price = undefined,
  clearPrice = false,
  notes = undefined,
  ...other
}) => async (dispatch, getState) => {
  const currentOrder = getProductInOrderByIndex(orderIndex)(getState());
  if (name && currentOrder.name !== name) {
    await dispatch({
      type: shoppingCartActionTypes.UPDATE_ORDER_NAME,
      payload: { name, orderIndex },
    });
  }

  // != since values can be either string or a number or both (ex -1 & '-1') - simplifies comparison
  if (amount && currentOrder.amount != amount) {
    await dispatch(updateOrderAmount(amount, orderIndex, false));
  }

  if (manualDiscount || manualDiscount === 0) {
    await dispatch(updateOrderDiscount(manualDiscount, orderIndex, false));
  }

  if (employeeID && currentOrder.employeeID !== employeeID) {
    await dispatch({
      type: shoppingCartActionTypes.SET_EMPLOYEE,
      payload: {
        orderIndex,
        employeeID,
      },
    });
  }

  if (clearPrice) {
    await dispatch(clearOrderPrice(orderIndex));
  } else if (price && currentOrder.price != price) {
    // Update price only if there's a difference
    await dispatch(updateOrderPrice(price, undefined, orderIndex));
  }
  if (notes) {
    await dispatch(updateProductNotes(notes, orderIndex));
  }

  await dispatch({
    type: shoppingCartActionTypes.UPDATE_ORDER_OTHER,
    payload: {
      orderIndex,
      ...other,
    },
  });
  dispatch(calculate());
};

export const clearSaleDiscount = () =>
  recalculateAfter({
    type: shoppingCartActionTypes.RESET_DISCOUNT,
    payload: {},
  });

export const applySaleDiscount = ({ amount, percentage }) => async (
  dispatch,
  getState,
) => {
  const maxDiscount = getDiscountLimit(getState());
  const { before, on, after } = getPluginLifecycleHook('onApplyDiscount')(
    getState(),
  );

  const products = getProductsInShoppingCart(getState());
  const params = { amount, percentage, products };
  await dispatch(before(params));

  if (!getHasProductsInShoppingCart(getState())) {
    dispatch(clearGlobalDiscountReason());
    return;
  }
  let total = getDiscountableTotal(getState());
  total = await dispatch(on(params, total));
  const targetPercentage = amount ? (100 * amount) / total : percentage;

  let payload = { amount, percentage, actual: targetPercentage / 100 };

  const discountLimit = Math.min(100, maxDiscount);
  if (targetPercentage > discountLimit) {
    // Load namespace in to avoid untranslated texts
    await i18next.loadNamespaces('managerOverride');

    payload = await dispatch(
      openManagerOverride([
        {
          key: 'maxDiscount',
          text: i18next.t('managerOverride:reasons.maxDiscount', {
            target: targetPercentage,
            limit: maxDiscount,
          }),
          isPermitted: user =>
            getRightsForUserID(Number(user.userID)).then(
              ({ maxDiscount }) =>
                targetPercentage === 0 || // Not setting a discount
                maxDiscount === 0 || // No discount limit
                Math.abs(targetPercentage) <= maxDiscount, // Discount is below limit
            ),
        },
      ]),
      // Use desired discount if got permission
      // otherwise use current user limit
    )
      .then(
        () => targetPercentage,
        () => {
          // Notify user about the actual applied discount in case manager override was cancelled
          dispatch(
            addWarning(
              i18next.t(
                'shoppingCart:alerts.maxDiscountManagerOverrideCancelled',
                {
                  discount: maxDiscount,
                },
              ),
            ),
          );
          return (
            Math.sign(targetPercentage) *
            Math.min(
              Math.max(0, maxDiscount), // Negative limits count as no discount allowed
              Math.abs(targetPercentage), // Otherwise take either the desired discount or max discount, whichever is smaller
            )
          );
        },
      )
      .then(async discount => {
        const reduction = discount / targetPercentage;
        return {
          amount: amount * reduction,
          percentage: percentage * reduction,
          actual: discount / 100,
        };
      });
  }

  batch(() => {
    dispatch({
      type: shoppingCartActionTypes.APPLY_DISCOUNT,
      payload,
    });
    dispatch(calculate());
  });

  await dispatch(after(params, payload));
};

export const applyPromotion = promotion =>
  recalculateAfter({
    type: shoppingCartActionTypes.APPLY_PROMOTION,
    payload: promotion,
  });

export const removePromotion = id =>
  recalculateAfter({
    type: shoppingCartActionTypes.REMOVE_PROMOTION,
    payload: id,
  });

export const applyCoupons = coupons =>
  recalculateAfter({
    type: shoppingCartActionTypes.APPLY_COUPONS,
    payload: coupons,
  });

export const applyCoupon = coupon =>
  recalculateAfter({
    type: shoppingCartActionTypes.APPLY_COUPONS,
    payload: [coupon],
  });

export const removeCoupon = id =>
  recalculateAfter({
    type: shoppingCartActionTypes.REMOVE_COUPON,
    payload: id,
  });

export const setVatRateForSale = vatRateId =>
  recalculateAfter({
    type: shoppingCartActionTypes.SET_VATRATE,
    payload: vatRateId,
  });

export const saleIsNotAnOffer = () => ({
  type: shoppingCartActionTypes.IS_NOT_OFFER,
});

export const setSelectedOffers = id => ({
  type: shoppingCartActionTypes.SET_SELECTED_OFFERS,
  payload: id,
});

export const setDiscountReason = (reason, orderIndex) => ({
  type: shoppingCartActionTypes.SET_DISCOUNT_REASON,
  payload: { reason, orderIndex },
});

export const setReturnReason = (reason, orderIndex) => ({
  type: shoppingCartActionTypes.SET_RETURN_REASON,
  payload: { reason, orderIndex },
});

export const clearReturnReasons = () => ({
  type: shoppingCartActionTypes.CLEAR_RETURN_REASONS,
});

export const clearDiscountReasons = () => ({
  type: shoppingCartActionTypes.CLEAR_DISCOUNT_REASONS,
});

export const clearDiscountReason = orderIndex => ({
  type: shoppingCartActionTypes.CLEAR_DISCOUNT_REASON,
  payload: orderIndex,
});

export const clearReturnReason = orderIndex => ({
  type: shoppingCartActionTypes.CLEAR_RETURN_REASON,
  payload: orderIndex,
});

export const clearGlobalReturnReason = () => ({
  type: shoppingCartActionTypes.CLEAR_GLOBAL_RETURN_REASON,
});

export const clearGlobalDiscountReason = () => ({
  type: shoppingCartActionTypes.CLEAR_GLOBAL_DISCOUNT_REASON,
});
